/**
 * WizardScreen — a single question rendered in conversational style.
 *
 * The entire screen slides in with .wizard-screen-enter when the question
 * changes (keyed by question.num). Renders AnswerChips below the question
 * text, and an AIChat widget as a collapsible below that.
 */

import { useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AnswerChips } from './AnswerChips';
import { AIChat } from './AIChat';
import { cn } from '@/lib/utils';
import type { Question, AnswerValue } from '@/types/rca';

// ============================================================
// Types
// ============================================================

interface WizardScreenProps {
  question: Question;
  phaseId: string;
  selected: AnswerValue | null;
  onAnswer: (value: AnswerValue) => void;
  onNext: () => void;
  onPrev: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  isLastQuestion: boolean;
}

// ============================================================
// Component
// ============================================================

export function WizardScreen({
  question,
  phaseId,
  selected,
  onAnswer,
  onNext,
  onPrev,
  canGoNext,
  canGoPrev,
  isLastQuestion,
}: WizardScreenProps) {
  // Unique key for AIChat conversation — phase + question num
  const questionId = `${phaseId}_${question.num}`;

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === 'Enter' && selected !== null && canGoNext) {
        e.preventDefault();
        onNext();
      }
      if ((e.key === 'Backspace' || e.key === 'ArrowLeft') && canGoPrev) {
        e.preventDefault();
        onPrev();
      }
    },
    [selected, canGoNext, canGoPrev, onNext, onPrev],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const displayText = question.conversational ?? question.text;

  return (
    // key={question.num} is set on the parent — this div gets the animation class
    <div className="wizard-screen-enter w-full max-w-2xl mx-auto flex flex-col gap-6 px-4">
      {/* Parent label for grouped sub-questions */}
      {question.parentLabel && (
        <p className="text-sm font-medium text-muted-foreground italic -mb-4">
          {question.parentLabel}
        </p>
      )}

      {/* Question header */}
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-foreground leading-snug text-center">
          {displayText}
        </h2>

        {/* Weight badge */}
        <div className="flex items-center justify-center gap-2">
          <Badge
            variant="outline"
            className="text-xs text-muted-foreground border-muted"
          >
            Weight: {question.weight}
          </Badge>
          {question.num && (
            <Badge
              variant="outline"
              className="text-xs text-muted-foreground border-muted"
            >
              Q{question.num}
            </Badge>
          )}
        </div>

        {/* Help text if present */}
        {question.help && (
          <p className="text-sm text-muted-foreground text-center bg-muted/50 rounded-lg px-4 py-2 max-w-lg mx-auto">
            {question.help}
          </p>
        )}
      </div>

      {/* Answer chips */}
      <AnswerChips
        options={question.answers}
        selected={selected as number | 'NA' | null}
        onSelect={onAnswer}
        allowNA={question.allowNA}
      />

      {/* AI Chat widget */}
      <AIChat
        question={question}
        questionId={questionId}
        onAnswer={onAnswer}
      />

      {/* Navigation row */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          type="button"
          variant="ghost"
          onClick={onPrev}
          disabled={!canGoPrev}
          className="gap-1.5 text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>

        <Button
          type="button"
          onClick={onNext}
          disabled={!canGoNext || selected === null}
          className={cn(
            'gap-1.5 bg-cot-blue hover:bg-cot-blue/90 text-white min-w-[120px]',
            'disabled:opacity-40',
          )}
        >
          {isLastQuestion ? (
            <>
              <CheckCircle2 className="w-4 h-4" />
              Finish
            </>
          ) : (
            <>
              Next
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>

      {/* Keyboard hint */}
      <p className="text-xs text-center text-muted-foreground/50">
        Press <kbd className="font-mono bg-muted px-1 py-0.5 rounded">Enter</kbd> to advance
        {canGoPrev && (
          <>, <kbd className="font-mono bg-muted px-1 py-0.5 rounded">Backspace</kbd> to go back</>
        )}
      </p>
    </div>
  );
}
